import * as React from 'react';
import Collapse from 'rc-collapse';
import Flex from '../../components/Flex/Flex';
import Text from '../../components/Text/Text';
import CaretDownIcon from '../../assets/Icons/CaretDownIcon.svg?react';
import classNames from 'classnames';
import motion from '../../utils/motionUtil.ts';
import preflop from '../../assets/preflop.png';
import postflop from '../../assets/postflop.png';
import V2FAQStatsPreview from '../../v2/components/V2FAQStatsPreview';
import V2FAQLayoutGuide from '../../v2/components/V2FAQLayoutGuide';
import V2FAQRandomizerGuide from '../../v2/components/V2FAQRandomizerGuide';
import V2FAQAutoCloseGuide from '../../v2/components/V2FAQAutoCloseGuide';
import portable1 from '../../assets/portable1.png';
import portable2 from '../../assets/portable2.png';
import portable3 from '../../assets/portable3.png';
import bookmode1 from '../../assets/bookmode1.png';
import bookmode2 from '../../assets/bookmode2.png';
import bookmode3 from '../../assets/bookmode3.png';
import randomizer1 from '../../assets/randomizer1.png';
import randomizer2 from '../../assets/randomizer2.png';
import randomizer3 from '../../assets/randomizer3.png';
import starscaption from '../../assets/starscaption.png';
import jurojin1 from '../../assets/jurojin1.png';
import jurojin2 from '../../assets/jurojin2.png';
import jurojin3 from '../../assets/jurojin3.png';
import jurojin4 from '../../assets/jurojin4.png';
import autoCloseChromeStep1 from '../../assets/faq/auto-close-chrome-step1.png';
import autoCloseChromeStep2 from '../../assets/faq/auto-close-chrome-step2.png';
import autoCloseChromeStep3 from '../../assets/faq/auto-close-chrome-step3.png';
import autoCloseChromeStep4 from '../../assets/faq/auto-close-chrome-step4.png';
import autoCloseChromeStep5 from '../../assets/faq/auto-close-chrome-step5.png';
import autoCloseSiteSettings from '../../assets/faq/auto-close-site-settings.png';
import { useTranslation } from 'react-i18next';

const MANAGER_TELEGRAM_URL = 'https://t.me/fishh_support';

const FAQ_STATS_SECTIONS = [
  { titleKey: 'faq_stats_section_preflop', bodyKey: 'faq_stats_preflop_body' },
  { titleKey: 'faq_stats_section_preflop_or', bodyKey: 'faq_stats_preflop_or_body' },
  { titleKey: 'faq_stats_section_postflop', bodyKey: 'faq_stats_postflop_body' },
  { titleKey: 'faq_stats_section_vscb', bodyKey: 'faq_stats_vscb_body' },
  { titleKey: 'faq_stats_section_cbet', bodyKey: 'faq_stats_cbet_body' },
  { titleKey: 'faq_stats_section_bx', bodyKey: 'faq_stats_bx_body' },
  { titleKey: 'faq_stats_section_donk', bodyKey: 'faq_stats_donk_body' },
] as const;

const FAQ_AUTO_CLOSE_IMAGES = [
  {
    src: autoCloseChromeStep1,
    captionKey: 'faq_panel7_img1_caption',
  },
  {
    src: autoCloseChromeStep2,
    captionKey: 'faq_panel7_img2_caption',
  },
  {
    src: autoCloseChromeStep3,
    captionKey: 'faq_panel7_img3_caption',
  },
  {
    src: autoCloseChromeStep4,
    captionKey: 'faq_panel7_img4_caption',
  },
  {
    src: autoCloseChromeStep5,
    captionKey: 'faq_panel7_img5_caption',
  },
  {
    src: autoCloseSiteSettings,
    captionKey: 'faq_panel7_img6_caption',
  },
] as const;

type FAQContentProps = {
  embedded?: boolean;
};

const FAQContent = React.memo(({ embedded = false }: FAQContentProps) => {
  const { t } = useTranslation('translation');

  const expandIcon = ({ isActive }: { isActive?: boolean }) => {
    return (
      <i
        className={classNames(
          embedded ? 'faq-accordion__icon' : 'PanelCollapse__icon',
          embedded
            ? { 'faq-accordion__icon--active': isActive }
            : { 'PanelCollapse__icon--active': isActive },
        )}
      >
        <CaretDownIcon />
      </i>
    );
  };

  const items = [
    {
      label: <Text size={4}>{t('faq_panel1_title')}</Text>,
      children: (
        <Flex vertical gap={24}>
          {embedded ? (
            <V2FAQStatsPreview />
          ) : (
            <Flex justify="space-between">
              <div>
                <img src={preflop} alt="" />
              </div>
              <div>
                <img src={postflop} alt="" />
              </div>
            </Flex>
          )}
          <Flex vertical gap={8}>
            <Text size={3} className="FAQPage__stats-intro">
              {t('faq_stats_intro')}
            </Text>
            {FAQ_STATS_SECTIONS.map(({ titleKey, bodyKey }) => (
              <Flex key={titleKey} vertical gap={6} className="FAQPage__stats-section">
                <Text size={3} bold>
                  {t(titleKey)}
                </Text>
                <Text size={3} className="FAQPage__stats-body">
                  {t(bodyKey)}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      ),
    },
    {
      label: <Text size={4}>{embedded ? t('faq_layout_title') : t('faq_panel2_title')}</Text>,
      children: embedded ? (
        <Flex vertical gap={20}>
          <Text size={3} className="FAQPage__stats-intro">
            {t('faq_layout_intro')}
          </Text>
          <V2FAQLayoutGuide />
        </Flex>
      ) : (
        <Flex vertical gap={32}>
          <Flex vertical gap={16}>
            <Text size={3}>{t('faq_panel2_content1')}</Text>
            <div>
              <img src={portable1} alt="" />
            </div>
          </Flex>
          <Flex vertical gap={16}>
            <Text size={3}>{t('faq_panel2_content2')}</Text>
            <img src={portable2} alt="" />
          </Flex>

          <Flex vertical gap={16}>
            <Text size={3}>
              {t('faq_panel2_content3_1')}
              <b>{t('faq_panel2_content3_2')}</b>
            </Text>
            <div>
              <img src={portable3} alt="" />
            </div>
          </Flex>
        </Flex>
      ),
    },
    ...(embedded
      ? []
      : [
          {
            label: <Text size={4}>{t('faq_panel3_title')}</Text>,
            children: (
              <Flex vertical gap={32}>
                <Flex vertical gap={16}>
                  <Text size={3}>{t('faq_panel3_content1')}</Text>
                  <div>
                    <img src={bookmode1} alt="" />
                  </div>
                </Flex>
                <Flex vertical gap={16}>
                  <Text size={3}>{t('faq_panel3_content2')}</Text>
                  <Flex gap={24}>
                    <img src={bookmode2} alt="" />
                    <div>
                      <img src={bookmode3} alt="" />
                    </div>
                  </Flex>
                </Flex>
              </Flex>
            ),
          },
        ]),
    {
      label: <Text size={4}>{t('faq_panel4_title')}</Text>,
      children: embedded ? (
        <Flex vertical gap={20}>
          <Text size={3} className="FAQPage__stats-intro">
            {t('faq_randomizer_intro')}
          </Text>
          <V2FAQRandomizerGuide />
        </Flex>
      ) : (
        <Flex vertical gap={32}>
          <Flex vertical gap={16}>
            <Text size={3}>{t('faq_panel4_content1')}</Text>
            <div>
              <img src={randomizer1} alt="" />
            </div>
          </Flex>
          <Flex vertical gap={16}>
            <Text size={3}>{t('faq_panel4_content2')}</Text>
            <Flex gap={24}>
              <div>
                <img src={randomizer2} alt="" />
              </div>
              <div>
                <img src={randomizer3} alt="" />
              </div>
            </Flex>
          </Flex>
        </Flex>
      ),
    },
    {
      label: <Text size={4}>{t('faq_panel6_title')}</Text>,
      children: (
        <Flex vertical gap={24}>
          <Text size={3}>{t('faq_panel6_content1')}</Text>

          <Flex className="FAQPage__TelegramCallout" vertical gap={12}>
            <Text size={3} bold>
              {t('faq_panel6_telegram_title')}
            </Text>
            <Text size={3}>{t('faq_panel6_telegram_text')}</Text>
            <a
              href={MANAGER_TELEGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="FAQPage__TelegramLink FAQPage__TelegramLink--button"
            >
              {t('faq_panel6_telegram_link')}
            </a>
            <a
              href={MANAGER_TELEGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="FAQPage__TelegramLink FAQPage__TelegramLink--url"
            >
              {MANAGER_TELEGRAM_URL}
            </a>
          </Flex>

          <Flex vertical gap={16}>
            <Flex vertical>
              <Text size={3} bold>
                {t('faq_panel6_step1_title')}
              </Text>
              <Text size={3}>{t('faq_panel6_step1_content')}</Text>
            </Flex>
            <Flex vertical>
              <Text size={3} bold>
                {t('faq_panel6_step2_title')}
              </Text>
              <Text size={3}>{t('faq_panel6_step2_content')}</Text>
            </Flex>
            <Flex vertical>
              <Text size={3} bold>
                {t('faq_panel6_step3_title')}
              </Text>
              <Text size={3}>{t('faq_panel6_step3_content')}</Text>
            </Flex>
            <Flex vertical>
              <Text size={3} bold>
                {t('faq_panel6_step4_title')}
              </Text>
              <Text size={3}>{t('faq_panel6_step4_content')}</Text>
            </Flex>
          </Flex>

          <Flex vertical>
            <Text size={3} bold>
              {t('faq_panel6_settings_title')}
            </Text>
            <Text size={3}>{t('faq_panel6_settings_content')}</Text>
          </Flex>

          <Flex vertical gap={8}>
            <Text size={3} bold>
              {t('faq_panel6_notes_title')}
            </Text>
            <Text size={3}>• {t('faq_panel6_note1')}</Text>
            <Text size={3}>• {t('faq_panel6_note2')}</Text>
            <Text size={3}>• {t('faq_panel6_note3')}</Text>
            <Text size={3}>• {t('faq_panel6_note4')}</Text>
          </Flex>
        </Flex>
      ),
    },
    {
      label: <Text size={4}>{t('faq_panel7_title')}</Text>,
      children: embedded ? (
        <Flex vertical gap={20}>
          <Text size={3} className="FAQPage__stats-intro">
            {t('faq_panel7_content1')}
          </Text>

          <Flex className="FAQPage__TelegramCallout" vertical gap={12}>
            <Text size={3} bold>
              {t('faq_panel7_telegram_title')}
            </Text>
            <Text size={3}>{t('faq_panel7_telegram_text')}</Text>
            <a
              href={MANAGER_TELEGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="FAQPage__TelegramLink FAQPage__TelegramLink--button"
            >
              {t('faq_panel7_telegram_link')}
            </a>
            <a
              href={MANAGER_TELEGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="FAQPage__TelegramLink FAQPage__TelegramLink--url"
            >
              {MANAGER_TELEGRAM_URL}
            </a>
          </Flex>

          <V2FAQAutoCloseGuide />
        </Flex>
      ) : (
        <Flex vertical gap={24}>
          <Flex className="FAQPage__TelegramCallout" vertical gap={12}>
            <Text size={3} bold>
              {t('faq_panel7_telegram_title')}
            </Text>
            <Text size={3}>{t('faq_panel7_telegram_text')}</Text>
            <a
              href={MANAGER_TELEGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="FAQPage__TelegramLink FAQPage__TelegramLink--button"
            >
              {t('faq_panel7_telegram_link')}
            </a>
            <a
              href={MANAGER_TELEGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="FAQPage__TelegramLink FAQPage__TelegramLink--url"
            >
              {MANAGER_TELEGRAM_URL}
            </a>
          </Flex>

          <Text size={3}>{t('faq_panel7_content1')}</Text>

          <Flex vertical gap={16}>
            <Text size={3} bold>
              {t('faq_panel7_install_title')}
            </Text>
            <Text size={3}>{t('faq_panel7_install_intro')}</Text>
          </Flex>

          <Flex vertical gap={24}>
            {FAQ_AUTO_CLOSE_IMAGES.slice(0, 5).map(({ src, captionKey }) => (
              <Flex key={captionKey} vertical gap={12}>
                <img src={src} className="FAQPage__Collapse-image" alt={t(captionKey)} />
                <Text size={3} type="secondary">
                  {t(captionKey)}
                </Text>
              </Flex>
            ))}
          </Flex>

          <Flex vertical gap={16}>
            <Flex vertical>
              <Text size={3} bold>
                {t('faq_panel7_step2_title')}
              </Text>
              <Text size={3}>{t('faq_panel7_step2_content')}</Text>
            </Flex>
            <Flex vertical gap={12}>
              <img
                src={FAQ_AUTO_CLOSE_IMAGES[5].src}
                className="FAQPage__Collapse-image"
                alt={t(FAQ_AUTO_CLOSE_IMAGES[5].captionKey)}
              />
              <Text size={3} type="secondary">
                {t(FAQ_AUTO_CLOSE_IMAGES[5].captionKey)}
              </Text>
            </Flex>
            <Flex vertical>
              <Text size={3} bold>
                {t('faq_panel7_step3_title')}
              </Text>
              <Text size={3}>{t('faq_panel7_step3_content')}</Text>
            </Flex>
            <Flex vertical>
              <Text size={3} bold>
                {t('faq_panel7_step4_title')}
              </Text>
              <Text size={3}>{t('faq_panel7_step4_content')}</Text>
            </Flex>
          </Flex>

          <Flex vertical>
            <Text size={3} bold>
              {t('faq_panel7_settings_title')}
            </Text>
            <Text size={3}>{t('faq_panel7_settings_content')}</Text>
          </Flex>

          <Flex vertical gap={8}>
            <Text size={3} bold>
              {t('faq_panel7_notes_title')}
            </Text>
            <Text size={3}>• {t('faq_panel7_note1')}</Text>
            <Text size={3}>• {t('faq_panel7_note2')}</Text>
            <Text size={3}>• {t('faq_panel7_note3')}</Text>
            <Text size={3}>• {t('faq_panel7_note4')}</Text>
          </Flex>
        </Flex>
      ),
    },
    {
      label: <Text size={4}>{t('faq_panel5_title1')}</Text>,
      children: (
        <Flex vertical gap={32}>
          <Flex vertical gap={24}>
            <Text size={3} bold>
              {t('faq_panel5_title2')}
            </Text>
            <Flex vertical gap={16}>
              <Flex vertical>
                <Text size={3}>{t('faq_panel5_content1')}</Text>
                <Text size={3} type="primary" underline>
                  http://fishhunter.info/statistics?playername=[SCREENNAME]&room=ps
                </Text>
              </Flex>
              <Flex vertical>
                <Text size={3}>{t('faq_panel5_content2')}</Text>
                <Text size={3} type="primary" underline>
                  http://fishhunter.info/statistics?playername=[SCREENNAME]&room=888
                </Text>
              </Flex>
              <Flex vertical>
                <Text size={3}>{t('faq_panel5_content10')}</Text>
                <Text size={3} type="primary" underline>
                  http://fishhunter.info/statistics?playername=[SCREENNAME]&room=CPR
                </Text>
              </Flex>
            </Flex>
            <Flex vertical>
              <Text size={3}>{t('faq_panel5_content3')}</Text>
              <img src={starscaption} className="FAQPage__Collapse-image" />
            </Flex>
          </Flex>
          <Flex vertical gap={24}>
            <Flex vertical>
              <Text size={3} bold>
                {t('faq_panel5_title3')}
              </Text>
              <Text size={3}>{t('faq_panel5_content4')}</Text>
            </Flex>
            <Flex vertical gap={16}>
              <Flex vertical>
                <Text size={3}>{t('faq_panel5_content5')}</Text>
                <img src={jurojin1} className="FAQPage__Collapse-image" />
              </Flex>
              <Flex vertical>
                <Text size={3}>
                  {t('faq_panel5_content6_1')}
                  <b>{t('faq_panel5_content6_2')}</b>
                </Text>
                <Text size={3} type="primary" underline>
                  {'https://fishhunter.info/statistics?playername=<PLAYER>&room=chi'}
                </Text>
              </Flex>
              <Flex vertical>
                <img src={jurojin2} className="FAQPage__Collapse-image" />
              </Flex>
              <Text size={3}>{t('faq_panel5_content7')}</Text>
            </Flex>
          </Flex>
          <Flex vertical gap={16}>
            <Text size={3}>{t('faq_panel5_content8')}</Text>
            <Flex vertical>
              <Text size={3}>{t('faq_panel5_content5')}</Text>
              <img src={jurojin3} className="FAQPage__Collapse-image" />
            </Flex>
            <Flex vertical>
              <Text size={3}>
                {t('faq_panel5_content6_1')}
                <b>{t('faq_panel5_content6_2')}</b>
              </Text>
              <Text size={3} type="primary" underline>
                {'https://fishhunter.info/statistics?playername=<PLAYER>&room=wpn'}
              </Text>
            </Flex>
            <img src={jurojin4} className="FAQPage__Collapse-image" />
            <Text size={3}>{t('faq_panel5_content9')}</Text>
          </Flex>
          <Flex vertical>
            <Text size={3} bold>
              3. Ipoker
            </Text>
            <Text size={3}>Ссылка для ipokertools:</Text>
            <Text size={3} type="primary" underline>
              https://fishhunter.info/statistics?playername=[PN]&room=IP
            </Text>
            <Text size={3}>Ссылка для jurojin:</Text>
            <Text size={3} type="primary" underline>
              {'https://fishhunter.info/statistics?playername=<PLAYER>&room=IP'}
            </Text>
          </Flex>
        </Flex>
      ),
    },
  ];
  const collapse = (
    <Collapse
      items={items}
      accordion={true}
      expandIcon={expandIcon}
      openMotion={embedded ? undefined : motion}
      className={embedded ? 'faq-accordion' : 'PanelCollapse'}
    />
  );

  return collapse;
});

export default FAQContent;
